import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { GlobalConstants } from 'src/app/core/constants/global-constants.constant';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { PageInfo } from 'src/app/core/models/page-info';
import { StockInfoSearchRequest } from 'src/app/core/models/transport-operation/stock-info-search-request';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { StockMaintenanceService } from 'src/app/features/stock/service/stock-maintenance.service';
import { DeliveryOrderService } from 'src/app/features/transport-operation/services/delivery-order.service';
import { PoServiceService } from 'src/app/features/transport-operation/services/po-service.service';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';


@Component({
  selector: 'laas-stock-info-search',
  templateUrl: './stock-info-search.component.html',
  styleUrls: ['./stock-info-search.component.scss']
})
export class StockInfoSearchComponent extends SessionBaseComponent implements OnInit {

  readonly preferenceKey: string;
  readonly permissionCode = PermissionConstants.ViewStockInfoSearch
  readonly helpPermissionCode = PermissionConstants.ViewStockSKUSearch;
  readonly defaultSearchParams: any;
  currentLang: string = this.laasSetting.language;

  @ViewChild('headerTable') dataTable!: DataTableComponent;

  isSearchFormOpen: boolean = true;
  isHeaderTableOpen: boolean = true;
  searchFormGroup: FormGroup;
  searchParams: StockInfoSearchRequest = new StockInfoSearchRequest();

  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
   };

  sortInfo: [{ property: string; direction: number }] | null;

  facilityList: any[] = []
  plantMappingOptions: any[] = [];
  storerMappingOptions: any[] = [];
  brandOptions: any[] = [];
  specLocTypeOptions: any[] = [];
  columns: any[] = [];
  searchResults: any[] = [];
  isLoading: boolean = false;

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
    private dropdownOptionService: DropdownOptionsService,
    private stockMaintenanceService: StockMaintenanceService,
    private poServiceService: PoServiceService,
    private doService: DeliveryOrderService,
    private localStorageService: LocalStorageService
  ) {
    super(injector);
    this.preferenceKey = UserPreferenceConstants.StockInfoSearch;

    this.searchFormGroup = this.formBuilder.group({
      facility: [null, Validators.required],
      hubPlantId: [null, Validators.required],
      storer: [null, Validators.required],
      sku: null,
      dateCode: null,
      brand: null,
      specLocType: null
    });

    this.columns = [
      { field: 'facility', header: 'StockInfoSearchComponent.HeaderTable.Facility', sort: true },
      { field: 'storer', header: 'StockInfoSearchComponent.HeaderTable.Storer', sort: true },
      { field: 'sku', header: 'StockInfoSearchComponent.HeaderTable.SKU', sort: true },
      { field: 'dateCode', header: 'StockInfoSearchComponent.HeaderTable.DateCode', sort: true },
      { field: 'vendorLot', header: 'StockInfoSearchComponent.HeaderTable.VendorLot', sort: true },
      { field: 'vendorId', header: 'StockInfoSearchComponent.HeaderTable.VendorId', sort: true },
      { field: 'hubPlantId', header: 'StockInfoSearchComponent.HeaderTable.HubPlantId', sort: true },
      { field: 'specLocType', header: 'StockInfoSearchComponent.HeaderTable.SpecLocType', sort: true },
      { field: 'brand', header: 'StockInfoSearchComponent.HeaderTable.Brand', sort: true },
      { field: 'qtyAvailable', header: 'StockInfoSearchComponent.HeaderTable.QtyAvailable', sort: true },
      { field: 'qty', header: 'StockInfoSearchComponent.HeaderTable.Qty', sort: true },
      { field: 'qtyAllocated', header: 'StockInfoSearchComponent.HeaderTable.QtyAllocated', sort: true },
      { field: 'qtyPicked', header: 'StockInfoSearchComponent.HeaderTable.QtyPicked', sort: true },
      { field: 'caseId', header: 'StockInfoSearchComponent.HeaderTable.CaseId', sort: true },
      { field: 'uid', header: 'StockInfoSearchComponent.HeaderTable.UID', sort: true },
      { field: 'loc', header: 'StockInfoSearchComponent.HeaderTable.Loc', sort: true },
      { field: 'locId', header: 'StockInfoSearchComponent.HeaderTable.LocId', sort: true }
    ];

    this.sortInfo = null;
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.logger.info('StockInfoSearchComponent onInit.');

    this.poServiceService.getMappingFacilityOptions().subscribe({
      next: (result) =>{
        const seenCodes = new Set<string>();
        this.facilityList = result
          .filter((x: { optionDesc: string }) => {
            if (seenCodes.has(x.optionDesc)) return false;
            seenCodes.add(x.optionDesc);
            return true;
          })
          .map((x: { optionDesc: string; optionCode: string }) => ({
            label: x.optionDesc,
            value: x.optionDesc,
          }));
        this.searchFormGroup.get('facility')?.setValue(this.facilityList[0].value);
      }
    });

    this.dropdownOptionService.getBrandOptions(1)
      .subscribe({
        next: (options: any[]) => {
          this.brandOptions = options.map(x => ({ label: x.brandName, value: x.brandName }));
        }
    });

    this.doService.getSpecLocType().subscribe({
      next:(result) =>{
        const seenSpecLocTypes = new Set<string>();
        this.specLocTypeOptions = result.specLocTypeList
        .filter((x:{storer: any; facility: any; specLocType: string}) => {
          if (x.storer != 'WISTRON_BBP' || x.facility != 'LK') return false;
          if (seenSpecLocTypes.has(x.specLocType)) return false;
          seenSpecLocTypes.add(x.specLocType);
          return true;
        })
        .map((x: { specLocType: any;}) => ({
          label: x.specLocType,
          value: x.specLocType
        }));
      }
    })

    this.storerMappingOptions = ['WISTRON_BBP'];
    this.searchFormGroup.get('storer')?.setValue(this.storerMappingOptions[0]);
  }


  searchBtnClick(): void {
    if(this.searchFormGroup.invalid){
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
      return;
    }
    this.dataTable.loading = true;
    this.dataTable.setCurrentPage(0);
    this.searchStockInfo();
  }

  searchStockInfo(): void {
    this.searchParams = deepCopy(this.searchFormGroup.value);
    if(isNullOrEmpty(this.searchParams.facility) || isNullOrEmpty(this.searchParams.hubPlantId) || isNullOrEmpty(this.searchParams.storer)){
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
      this.dataTable.loading = false;
      return;
    }
    this.searchParams.sku = isNullOrEmpty(this.searchParams.sku)?null:this.searchParams.sku;
    this.searchParams.conditions = [
      {
        seqNo: 1,
        conditionType: 'DATE_CODE',
        condition: '=',
        conditionValue: this.searchFormGroup.controls['dateCode'].value,
      }
    ];
    this.searchParams.specLocType = isNullOrEmpty(this.searchFormGroup.controls['specLocType'].value)?[]:[this.searchFormGroup.controls['specLocType'].value];
    this.searchParams.pageSize = this.pageInfo.pageSize;
    this.searchParams.pageIndex = this.pageInfo.pageIndex;
    this.searchParams.sortInfo = this.sortInfo;

    console.log('searchParams',this.searchParams);
	  this.stockMaintenanceService.searchStockInfoByUid(this.searchParams)
    .pipe(finalize(()=>this.dataTable.loading = false))
    .subscribe({
      next: (resp: any) => {
        console.log('response',resp);
        this.searchResults = resp.data.inventoryData;
        this.pageInfo.totalRecords = resp.data.totalRecords;
        this.dataTable.setPageInfo(this.pageInfo.totalRecords);
        this.dataTable.updateTableData(this.searchResults);
      },
      error: (err) => {
        (isNullOrEmpty(err.error.errorMsg))?
        this.toastService.error(this.translateService.instant('Common.Message.UnknowError')):
        this.toastService.error(this.translateService.instant(err.error.errorMsg));

		    this.searchResults = [];
      },
      complete: () => {
        this.isSearchFormOpen = false;
      }
    })
  }

  resetBtnClick() {
    this.searchFormGroup.reset();
  }

  tableSort(event: any): void {
    this.sortInfo = [{ property: event.field, direction: event.order }];
    this.dataTable.setCurrentPage(0);
    this.searchBtnClick();
  }

  tablePageChange(event: any) {
	  this.pageInfo.pageSize = event.rows;
	  this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
	  this.searchBtnClick();
	}

  get language(): string {
    return this.localStorageService.getItem(GlobalConstants.languageKey);
  }

  async exportBtnClick(event: any): Promise<void> {
    this.dataTable.loading = true;
    console.log(this.searchParams);
    this.searchParams.conditions = [
      {
        seqNo: 1,
        conditionType: 'DATE_CODE',
        condition: '=',
        conditionValue: this.searchFormGroup.controls['dateCode'].value,
      }
    ];
    this.stockMaintenanceService.exportStockInfoExcel(this.searchParams,this.language)
    .pipe(finalize(() => { this.dataTable.loading = false; }))
    .subscribe({
      next: result => {
        if (result == null || result.size === 0) {
          this.toastService.info(this.translateService.instant('Common.Toast.NoDataFound'));
          this.loader.hide();
          return;
        }
        const file = result;
        const url = URL.createObjectURL(file);
        const a: any = document.createElement('a');
        file.name = 'Inventory.xlsx';
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: err => {
        this.toastErrorMessage(err.error);
      }
    });
  }
}
